import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Button
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import CopyableText from '../common/CopyableText';
import { useAccountUsers } from '../common/AccountUsersAutocomplete';
import SemanticTableService from '../../services/SemanticTableService';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const idsFrom = (list, keys) => {
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .map((item) => {
      if (!item) {
        return null;
      }
      if (typeof item === 'string') {
        return item;
      }
      const key = keys.find((candidate) => item[candidate]);
      return key ? item[key] : null;
    })
    .filter(Boolean);
};

const labelSx = { color: 'var(--wb-text-label)', mb: 0.5 };
const textSx = { color: 'var(--wb-text-soft)' };

/**
 * Nombre de un componente y, debajo, su identificador en azul tenue y letra
 * más chica. Los dos se pueden copiar.
 */
export const NamedId = ({ name, id, label }) => (
  <Box sx={{ minWidth: 0 }}>
    <CopyableText variant="body2" label={`nombre de ${label}`} sx={{ color: 'var(--wb-text)', fontWeight: 500 }}>
      {name || id || '—'}
    </CopyableText>
    {id && (
      <CopyableText
        variant="caption"
        label={`identificador de ${label}`}
        iconSize={12}
        sx={{ color: 'var(--wb-accent)', opacity: 0.75, fontSize: '0.7rem', lineHeight: 1.3 }}
      >
        {id}
      </CopyableText>
    )}
  </Box>
);

const SemanticModelViewDialog = ({
  open,
  onClose,
  record,
  database,
  accountName,
  accountId
}) => {
  const tableIds = useMemo(
    () => idsFrom(record?.contained_tables_id, ['table_id', 'TABLE_ID']),
    [record]
  );
  const userIds = useMemo(
    () => idsFrom(record?.users_allowed, ['user_id', 'USER_ID', 'id']),
    [record]
  );

  const { usersById } = useAccountUsers(open);
  const userLabel = (userId) => {
    const user = usersById.get(userId);
    return user?.email || user?.name || null;
  };

  // Nombres de las tablas: el modelo solo guarda sus ids.
  const databaseId = record?.semantid_database_id;
  const [tableNames, setTableNames] = useState(new Map());
  useEffect(() => {
    if (!open || !databaseId || tableIds.length === 0) {
      return undefined;
    }
    let cancelled = false;
    SemanticTableService.list({ accountId, semanticDatabaseId: databaseId, includeDeleted: true })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
        setTableNames(new Map(items.map((table) => [table.table_id, table.display_name || table.table_name])));
      })
      .catch((error) => console.warn('No se pudieron cargar los nombres de las tablas:', error));
    return () => {
      cancelled = true;
    };
  }, [open, databaseId, accountId, tableIds.length]);

  const modelType =
    record?.model_type ||
    record?.metadata?.model_type ||
    record?.METADATA?.model_type ||
    record?.METADATA?.MODEL_TYPE ||
    null;

  const auditUser = (userId) => (userId ? <NamedId name={userLabel(userId) || userId} id={userLabel(userId) ? userId : null} label="usuario" /> : <Typography variant="body2" sx={textSx}>—</Typography>);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-panel)',
          color: 'var(--wb-text-strong)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
        Detalles del modelo semántico
      </DialogTitle>
      <DialogContent sx={{ '&&': { pt: 3 } }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <CopyableText variant="h6" label="nombre del modelo" iconSize={16} sx={{ fontWeight: 600 }}>
              {record?.name || 'Modelo sin nombre'}
            </CopyableText>
            <CopyableText
              variant="caption"
              label="identificador del modelo"
              iconSize={12}
              sx={{ color: 'var(--wb-accent)', opacity: 0.75, fontSize: '0.7rem' }}
            >
              {record?.semantic_model_id || record?.id || ''}
            </CopyableText>
            <Box sx={{ mt: 1 }}>
              {record?.description ? (
                <CopyableText variant="body2" label="descripción" sx={textSx}>
                  {record.description}
                </CopyableText>
              ) : (
                <Typography variant="body2" sx={textSx}>Sin descripción registrada.</Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={labelSx}>Base semántica</Typography>
            <NamedId name={database?.name} id={databaseId} label="base semántica" />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={labelSx}>Cuenta asociada</Typography>
            <NamedId
              name={accountName}
              id={record?.user_accout_id || record?.user_account_id}
              label="cuenta"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={labelSx}>Estado</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={record?.soft_delete?.deleted ? 'Eliminado' : record?.is_active === false ? 'Inactivo' : 'Activo'}
                size="small"
                sx={{
                  color: record?.soft_delete?.deleted ? 'var(--wb-danger)' : record?.is_active === false ? 'var(--wb-warning)' : 'var(--wb-success-strong)',
                  backgroundColor: 'var(--wb-x-bg-94a3b824, rgba(148, 163, 184, 0.14))'
                }}
              />
              {record?.is_public && (
                <Chip
                  icon={<PublicIcon sx={{ color: 'var(--wb-accent) !important' }} />}
                  label="Público"
                  size="small"
                  sx={{ color: 'var(--wb-accent)', backgroundColor: 'var(--wb-x-bg-38bdf824, rgba(56, 189, 248, 0.14))' }}
                />
              )}
              {modelType && (
                <Chip
                  icon={<InsightsIcon sx={{ color: 'var(--wb-x-text-c4b5fd, #c4b5fd) !important' }} />}
                  label={modelType}
                  size="small"
                  sx={{ color: 'var(--wb-x-text-c7d2fe, #c7d2fe)', backgroundColor: 'var(--wb-x-bg-818cf829, rgba(129, 140, 248, 0.16))' }}
                />
              )}
              {record?.version_id && (
                <CopyableText variant="body2" label="versión" sx={textSx}>
                  {`Versión: ${record.version_id}`}
                </CopyableText>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={labelSx}>Tablas incluidas</Typography>
            {tableIds.length === 0 ? (
              <Typography variant="body2" sx={textSx}>No se registraron tablas.</Typography>
            ) : (
              <Grid container spacing={1.5}>
                {tableIds.map((tableId) => (
                  <Grid item xs={12} sm={6} key={tableId}>
                    <NamedId name={tableNames.get(tableId)} id={tableId} label="tabla" />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={labelSx}>Usuarios autorizados</Typography>
            {userIds.length === 0 ? (
              <Typography variant="body2" sx={textSx}>No se registraron usuarios.</Typography>
            ) : (
              <Grid container spacing={1.5}>
                {userIds.map((userId) => (
                  <Grid item xs={12} sm={6} key={userId}>
                    <NamedId name={userLabel(userId)} id={userId} label="usuario" />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={labelSx}>Creado por</Typography>
            {auditUser(record?.audit?.created_by)}
            <CopyableText variant="caption" label="fecha de creación" sx={{ ...textSx, display: 'block', mt: 0.5 }}>
              {formatDate(record?.audit?.created_at)}
            </CopyableText>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={labelSx}>Actualizado por</Typography>
            {auditUser(record?.audit?.updated_by)}
            <CopyableText variant="caption" label="fecha de actualización" sx={{ ...textSx, display: 'block', mt: 0.5 }}>
              {formatDate(record?.audit?.updated_at)}
            </CopyableText>
          </Grid>

          {record?.soft_delete?.deleted && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={labelSx}>Eliminado el</Typography>
              <CopyableText variant="body2" label="fecha de eliminación" sx={textSx}>
                {formatDate(record?.soft_delete?.deleted_at)}
              </CopyableText>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)' }}>
        <Button onClick={onClose} sx={{ color: 'var(--wb-muted)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SemanticModelViewDialog;
